import { useEffect, useRef, useState } from "react";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type ChatMessage,
  chatWithAssistant,
  getAssistantStatus,
} from "@/api/assistant";

const STORAGE_KEY = "cortex-assistant-history";

function loadHistory(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ChatMessage =>
        m && (m.role === "user" || m.role === "assistant") && typeof m.content === "string",
    );
  } catch {
    return [];
  }
}

function saveHistory(history: ChatMessage[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    /* quota / private mode — swallow */
  }
}

export function AssistantFab() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const endRef = useRef<HTMLDivElement | null>(null);

  const status = useQuery({
    queryKey: ["assistant-status"],
    queryFn: getAssistantStatus,
    staleTime: 10 * 60_000,
    retry: false,
  });

  useEffect(() => {
    setHistory(loadHistory());
  }, []);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, open]);

  const send = useMutation({
    mutationFn: (msg: string) => {
      const next: ChatMessage[] = [
        ...history,
        { role: "user", content: msg },
      ];
      setHistory(next);
      saveHistory(next);
      return chatWithAssistant(next);
    },
    onSuccess: (data) => {
      const next: ChatMessage[] = [
        ...history,
        { role: "user", content: input.trim() },
        { role: "assistant", content: data.reply },
      ];
      setHistory(next);
      saveHistory(next);
      setInput("");
    },
    onError: () => {
      // Roll back the optimistic user message
      const rolled = history.slice();
      setHistory(rolled);
      saveHistory(rolled);
    },
  });

  if (status.isLoading) return null;
  if (!status.data?.enabled) return null;

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    send.mutate(trimmed);
  };

  const clear = () => {
    setHistory([]);
    saveHistory([]);
  };

  return (
    <>
      <Tooltip title={t("assistant.fabTooltip")} placement="left">
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: "calc(24px + env(safe-area-inset-bottom))",
            right: "calc(24px + env(safe-area-inset-right))",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background:
              "linear-gradient(135deg, #2563FF 0%, #22D3EE 55%, #6D5DFB 100%)",
          }}
          aria-label={t("assistant.fabTooltip")}
        >
          <AutoAwesomeIcon />
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{ paper: { sx: { width: { xs: "100%", sm: 480 } } } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Header */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Avatar
              sx={{
                background:
                  "linear-gradient(135deg, #2563FF 0%, #22D3EE 55%, #6D5DFB 100%)",
                width: 32,
                height: 32,
              }}
            >
              <AutoAwesomeIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t("assistant.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("assistant.modelHint", { model: status.data.model ?? "—" })}
              </Typography>
            </Box>
            <Tooltip title={t("assistant.clear")}>
              <IconButton size="small" onClick={clear} disabled={history.length === 0}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {/* Message list */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: "background.default" }}>
            {history.length === 0 ? (
              <Alert severity="info" variant="outlined">
                {t("assistant.welcome")}
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                {history.map((m, i) => (
                  <MessageBubble key={i} message={m} />
                ))}
                {send.isPending && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      {t("assistant.thinking")}
                    </Typography>
                  </Stack>
                )}
                {send.error && (
                  <Alert severity="error" variant="outlined">
                    {(send.error as { response?: { status?: number; data?: { detail?: string } } })
                      .response?.data?.detail ?? t("assistant.error")}
                  </Alert>
                )}
                <Box ref={endRef} />
              </Stack>
            )}
          </Box>

          {/* Input */}
          <Box
            sx={{
              borderTop: 1,
              borderColor: "divider",
              p: 1.5,
              bgcolor: "background.paper",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                placeholder={t("assistant.placeholder")}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                size="small"
                disabled={send.isPending}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={send.isPending || !input.trim()}
              >
                <SendIcon />
              </IconButton>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5, fontSize: 11 }}
            >
              {t("assistant.disclaimer")}
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}


function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: "85%",
          px: 1.5,
          py: 1,
          bgcolor: isUser ? "primary.main" : "background.paper",
          color: isUser ? "primary.contrastText" : "text.primary",
          border: isUser ? 0 : 1,
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {message.content}
        </Typography>
      </Paper>
    </Box>
  );
}
